import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Star } from "@/components/landing/Logo";
import { Reveal } from "@/components/landing/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getPosts, getBlogFaq } from "@/data/blogPosts";
import { useAiPosts } from "@/data/useAiPosts";
import { SeasonEdit } from "@/components/SeasonEdit";
import { useLang } from "@/i18n/LanguageContext";

const fmtDate = (iso, lang) =>
  new Date(iso).toLocaleDateString({ de: "de-DE", en: "en-GB", es: "es-ES", it: "it-IT", fr: "fr-FR" }[lang] || "de-DE", { day: "numeric", month: "long", year: "numeric" });

const MAG = {
  de: { cover: "Titelstory der Woche", picks: "Editor's Choice", issue: "Ausgabe", ticker: ["Stil", "Farben", "Smart Mirror", "Glow-up", "Phygital Retail", "Ehrlich statt gefiltert"] },
  en: { cover: "Cover story of the week", picks: "Editor's Choice", issue: "Issue", ticker: ["Style", "Colours", "Smart Mirror", "Glow-up", "Phygital Retail", "Honest, not filtered"] },
  es: { cover: "Portada de la semana", picks: "Selección editorial", issue: "Edición", ticker: ["Estilo", "Colores", "Smart Mirror", "Glow-up", "Retail phygital", "Honesta, sin filtros"] },
  it: { cover: "Copertina della settimana", picks: "Scelta dell'editor", issue: "Edizione", ticker: ["Stile", "Colori", "Smart Mirror", "Glow-up", "Retail phygital", "Onesta, non filtrata"] },
  fr: { cover: "En couverture cette semaine", picks: "Choix de la rédaction", issue: "Édition", ticker: ["Style", "Couleurs", "Smart Mirror", "Glow-up", "Retail phygital", "Honnête, sans filtre"] },
};

const isoWeek = (d = new Date()) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  return Math.ceil(((t - new Date(Date.UTC(t.getUTCFullYear(), 0, 1))) / 86400000 + 1) / 7);
};

const SPANS = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4", "md:col-span-5", "md:col-span-7"];
const HEIGHTS = ["h-64", "h-56", "h-48", "h-48", "h-48", "h-56", "h-64"];

export default function Blog() {
  const { t, lang } = useLang();
  const b = t.blog;
  const cat = (p) => (p.category === "B2B" ? b.catB2B : p.audience === "men" ? b.catMen : p.audience === "women" ? b.catWomen : b.catB2C);
  const m = MAG[lang] || MAG.de;
  const [filter, setFilter] = useState(() => {
    const f = new URLSearchParams(window.location.search).get("f");
    return ["women", "men", "B2B"].includes(f) ? f : "all";
  });
  const { posts: aiPosts } = useAiPosts(lang);
  const allPosts = [...aiPosts, ...getPosts(lang)].sort((a, c) => new Date(c.date) - new Date(a.date));
  const blogFaq = getBlogFaq(lang);
  const posts =
    filter === "all"
      ? allPosts
      : filter === "B2B"
      ? allPosts.filter((p) => p.category === "B2B")
      : allPosts.filter((p) => p.category === "B2C" && (p.audience === filter || !p.audience));
  const covers = posts.slice(0, Math.min(4, posts.length));
  const [coverIdx, setCoverIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { setCoverIdx(covers.length ? isoWeek() % covers.length : 0); }, [filter, lang, covers.length]);
  useEffect(() => {
    if (paused || covers.length < 2) return;
    const id = setInterval(() => setCoverIdx((i) => (i + 1) % covers.length), 7000);
    return () => clearInterval(id);
  }, [paused, covers.length]);
  const cover = covers[coverIdx] || covers[0];
  const picks = posts.slice(covers.length, covers.length + 3);
  const rest = posts.slice(covers.length + 3);

  useEffect(() => {
    const timer = setTimeout(() => {
      document.title = `${b.kicker} | Mira Mirror`;
      const el = document.querySelector('meta[name="description"]');
      if (el) el.setAttribute("content", b.sub);
    }, 0);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "blog-faq-ld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: blogFaq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(ld);
    return () => {
      clearTimeout(timer);
      document.getElementById("blog-faq-ld")?.remove();
      document.title = t.seo.title;
      const el = document.querySelector('meta[name="description"]');
      if (el) el.setAttribute("content", t.seo.description);
    };
  }, [b, t.seo, lang]);

  const filters = [
    { key: "all", label: b.all },
    { key: "women", label: b.women },
    { key: "men", label: b.men },
    { key: "B2B", label: b.b2b },
  ];

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <Navbar />
      <div className="pt-32 sm:pt-36" data-testid="blog-page">
        {/* Masthead */}
        <Reveal>
          <header className="mx-auto max-w-7xl px-5 sm:px-8 text-center">
            <div className="flex items-center justify-center gap-4 text-[11px] uppercase font-bold" style={{ letterSpacing: "0.3em", color: "var(--camel)" }}>
              <span className="hidden h-px w-16 sm:block" style={{ background: "var(--gold)" }} />
              <Star size={11} /> Mira Mirror · {m.issue} 2026 <Star size={11} />
              <span className="hidden h-px w-16 sm:block" style={{ background: "var(--gold)" }} />
            </div>
            <h1 className="mt-5 font-serif-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight" data-testid="blog-title">
              {b.kicker}
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>{b.sub}</p>

            <div className="mt-8 flex flex-wrap justify-center gap-2 border-y py-4" style={{ borderColor: "var(--on-surface)" }} data-testid="blog-filters">
              {filters.map((f) => (
                <button
                  key={f.key}
                  data-testid={`blog-filter-${f.key.toLowerCase()}`}
                  onClick={() => setFilter(f.key)}
                  className="relative px-5 py-1.5 text-xs font-bold uppercase transition-colors duration-300"
                  style={{ letterSpacing: "0.12em", color: filter === f.key ? "var(--camel)" : "var(--muted-warm)" }}
                >
                  {f.label}
                  {filter === f.key && <span className="absolute inset-x-4 -bottom-[17px] h-[3px]" style={{ background: "var(--gold)" }} />}
                </button>
              ))}
            </div>
          </header>
        </Reveal>

        {/* Cover story + Editor's Choice */}
        {cover && (
          <section className="mx-auto mt-10 max-w-7xl px-5 sm:px-8">
            <div className="grid grid-cols-12 gap-6">
              <Reveal className="col-span-12 lg:col-span-8">
                <div
                  className="relative h-full min-h-[420px] overflow-hidden rounded-[2rem]"
                  onMouseEnter={() => setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                  data-testid="magazine-cover-rotator"
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={cover.slug}
                      initial={{ opacity: 0, scale: 1.03 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="absolute inset-0"
                    >
                      <Link to={`/blog/${cover.slug}`} data-testid="magazine-cover-story" className="group relative block h-full w-full">
                        <img src={cover.image} alt={cover.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105" />
                        <div className="absolute inset-0" style={{ background: "linear-gradient(10deg, rgba(28,22,18,0.82) 8%, rgba(28,22,18,0.25) 55%, rgba(28,22,18,0.05) 100%)" }} />
                        <div className="absolute inset-x-5 top-5 sm:inset-x-6 sm:top-6 flex flex-wrap items-start justify-between gap-2">
                          <span className="-rotate-3 rounded-full px-4 py-1.5 text-[10px] font-black uppercase text-white shadow-lg" style={{ background: "var(--camel)", letterSpacing: "0.18em" }}>
                            ✦ {m.cover}
                          </span>
                          <span className="rotate-2 rounded-full border border-white/40 px-3 py-1 text-[10px] font-bold uppercase text-white/90 backdrop-blur-sm" style={{ letterSpacing: "0.12em", background: "rgba(28,22,18,0.35)" }}>
                            {cover.readTime} {b.min} ✦ {cat(cover)}
                          </span>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 p-7 pb-12 sm:p-9 sm:pb-14">
                          <p className="text-[11px] font-bold uppercase text-white/70" style={{ letterSpacing: "0.2em" }}>{fmtDate(cover.date, lang)}</p>
                          <h2 className="mt-2 max-w-2xl font-serif-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.08] text-white">{cover.title}</h2>
                          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80 line-clamp-2">{cover.excerpt}</p>
                          <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold transition-transform duration-300 group-hover:translate-x-1.5" style={{ color: "var(--gold)" }}>
                            {b.readMore} <ArrowRight size={15} />
                          </span>
                        </div>
                      </Link>
                    </motion.div>
                  </AnimatePresence>
                  {covers.length > 1 && (
                    <div className="absolute bottom-5 left-7 sm:left-9 z-10 flex items-center gap-2" data-testid="cover-rotator-dots">
                      {covers.map((c, i) => (
                        <button
                          key={c.slug}
                          aria-label={c.title}
                          data-testid={`cover-dot-${i}`}
                          onClick={() => setCoverIdx(i)}
                          className="h-[5px] overflow-hidden rounded-full transition-all duration-500"
                          style={{ width: i === coverIdx ? 34 : 10, background: i === coverIdx ? "var(--gold)" : "rgba(255,255,255,0.45)" }}
                        >
                          {i === coverIdx && !paused && (
                            <motion.span key={cover.slug} className="block h-full" style={{ background: "var(--camel)" }} initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 7, ease: "linear" }} />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>

              <div className="col-span-12 lg:col-span-4" data-testid="magazine-editor-picks">
                <Reveal delay={0.1}>
                  <p className="text-[11px] font-black uppercase" style={{ letterSpacing: "0.22em", color: "var(--camel)" }}>✦ {m.picks}</p>
                </Reveal>
                <div className="mt-4 flex flex-col divide-y" style={{ borderColor: "var(--line)" }}>
                  {picks.map((p, i) => (
                    <Reveal key={p.slug} delay={0.12 + i * 0.08}>
                      <Link to={`/blog/${p.slug}`} data-testid={`editor-pick-${p.slug}`} className="group flex gap-4 py-4 first:pt-0" style={{ borderColor: "var(--line)" }}>
                        <span className="font-serif-display text-4xl font-bold leading-none" style={{ color: "var(--gold)" }}>0{i + 1}</span>
                        <div className="flex-1">
                          <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: "0.14em", color: "var(--muted-warm)" }}>
                            {cat(p)} · {p.readTime} {b.min}
                          </p>
                          <h3 className="mt-1 font-serif-display text-lg font-semibold leading-snug transition-colors duration-300 group-hover:text-[var(--camel)]">{p.title}</h3>
                        </div>
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                          <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </div>
                      </Link>
                    </Reveal>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Kinetic ticker */}
        <div className="mt-14 overflow-hidden border-y py-3" style={{ borderColor: "var(--on-surface)", background: "var(--surface-secondary)" }} data-testid="magazine-ticker">
          <div className="mira-marquee flex w-max items-center gap-8">
            {[...Array(3)].flatMap((_, r) =>
              m.ticker.map((w, i) => (
                <span key={`${r}-${i}`} className="flex items-center gap-8 whitespace-nowrap font-serif-display text-xl italic" style={{ color: r === 1 ? "var(--camel)" : "var(--on-surface)" }}>
                  {w} <Star size={12} />
                </span>
              ))
            )}
          </div>
        </div>

        {/* Season edit */}
        <SeasonEdit posts={allPosts} />

        {/* Asymmetric grid */}
        <section className="mx-auto mt-12 max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {rest.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 0.08} className={`md:col-span-6 ${SPANS[i % SPANS.length]}`}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_32px_56px_-28px_rgba(198,156,109,0.5)]"
                  style={{ background: "var(--surface-secondary)", borderColor: "var(--line)" }}
                >
                  <div className={`relative overflow-hidden ${HEIGHTS[i % HEIGHTS.length]}`}>
                    <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute left-4 top-4 -rotate-2 rounded-full px-3 py-1 text-[9px] font-black uppercase text-white" style={{ background: "rgba(28,22,18,0.75)", letterSpacing: "0.14em", backdropFilter: "blur(6px)" }}>
                      {post.readTime} {b.min} ✦ {cat(post)}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <span className="text-[10px] font-bold uppercase" style={{ letterSpacing: "0.16em", color: "var(--muted-warm)" }}>{fmtDate(post.date, lang)}</span>
                    <h2 className="mt-2.5 font-serif-display text-xl sm:text-2xl font-semibold leading-snug">{post.title}</h2>
                    <p className="mt-2.5 flex-1 text-sm leading-relaxed line-clamp-3" style={{ color: "var(--muted-warm)" }}>{post.excerpt}</p>
                    <span className="mt-4 flex items-center gap-1.5 text-sm font-semibold transition-transform duration-300 group-hover:translate-x-1" style={{ color: "var(--camel)" }}>
                      {b.readMore} <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto mt-24 max-w-3xl px-5 pb-24 sm:px-8" data-testid="blog-faq-section">
          <Reveal className="text-center">
            <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
              <Star size={11} /> FAQ
            </p>
            <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl font-semibold">{b.faqTitle}</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="space-y-3">
              {blogFaq.map((item, i) => (
                <AccordionItem key={i} value={`bf-${i}`} className="rounded-2xl border px-6" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid={`blog-faq-item-${i + 1}`}>
                  <AccordionTrigger className="py-5 text-left font-serif-display text-lg font-semibold hover:no-underline [&>svg]:text-[var(--camel)]">{item.q}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-sm sm:text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>
      </div>
      <Footer />
    </main>
  );
}
